import { useCallback, useMemo, useRef, useState } from "react";

export interface DirtyTracking<T> {
  values: T;
  setValues: (next: T) => void;
  setField: <K extends keyof T>(key: K, value: T[K]) => void;
  isDirty: boolean;
  dirtyFields: ReadonlySet<keyof T>;
  dirtyCount: number;
  reset: () => void;
  markSaved: (next: T) => void;
}

function shallowEqual<T extends object>(a: T, b: T): boolean {
  const ak = Object.keys(a) as (keyof T)[];
  const bk = Object.keys(b) as (keyof T)[];
  if (ak.length !== bk.length) return false;
  for (const k of ak) if (a[k] !== b[k]) return false;
  return true;
}

/**
 * Track the diff between a current form snapshot and the last saved
 * snapshot. Used by the defaults pane (and later the edit screen) to
 * surface "N unsaved fields" and to fuel the discard / preview-gated
 * save UX.
 */
export function useDirtyTracking<T extends object>(
  initial: T,
): DirtyTracking<T> {
  const [values, setValuesState] = useState<T>(initial);
  const savedRef = useRef<T>(initial);

  const setValues = useCallback((next: T) => {
    setValuesState(next);
  }, []);

  const setField = useCallback(
    <K extends keyof T>(key: K, value: T[K]) => {
      setValuesState((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const reset = useCallback(() => {
    setValuesState(savedRef.current);
  }, []);

  const markSaved = useCallback((next: T) => {
    savedRef.current = next;
    setValuesState(next);
  }, []);

  const dirtyFields = useMemo(() => {
    const set = new Set<keyof T>();
    const saved = savedRef.current;
    for (const key of Object.keys(values) as (keyof T)[]) {
      if (values[key] !== saved[key]) set.add(key);
    }
    return set;
    // shallowEqual reference is intentional — savedRef changes don't trigger
    // re-renders, but we recompute on every render anyway since the saved
    // ref is captured here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const isDirty = !shallowEqual(values, savedRef.current);

  return {
    values,
    setValues,
    setField,
    isDirty,
    dirtyFields,
    dirtyCount: dirtyFields.size,
    reset,
    markSaved,
  };
}
